import * as React from "react";
import { ICustomSelectorProps, SRSHCustomSelector } from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { DogApi, DogSearch, DogGridDto } from "../../../../api";
import { useGlobalData } from "../../../../contexts";
import { IDataTableColumn, Loading } from "nsitools-react";
import { ETLCodes } from "../../../../locales";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../../ERoutes";

export interface IBroodRequestPotentialFatherSelectorProps
  extends Pick<ICustomSelectorProps<any>, "linkedIds" | "onLinkedIdsChanged"> {
  editMode: boolean;
  loading: boolean;
  clickableDogs?: boolean;
  motherId: number;
  showAllButtons?: boolean;
  formWidth: number;
}

export const BroodRequestPotentialFatherSelector: React.FunctionComponent<
  IBroodRequestPotentialFatherSelectorProps
> = ({
  editMode,
  linkedIds,
  onLinkedIdsChanged,
  loading,
  motherId,
  clickableDogs = true,
  showAllButtons = false,
  formWidth,
}) => {
  const api = useApiService(DogApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const tooSmall = React.useMemo(() => formWidth < 1700, [formWidth]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Name),
        fieldName: "computedName",
        autoFitContent: tooSmall,
        render: (row: DogGridDto) =>
          clickableDogs ? (
            <Link target={"_blank"} to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
              {row.computedName}
            </Link>
          ) : (
            <div>{row.computedName}</div>
          ),
      },
      !tooSmall && {
        header: () => t(ETLCodes.Pedigree),
        fieldName: "pedigree",
      },
      !tooSmall && {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.DogChips),
        fieldName: "dogChips",
        render: (row: DogGridDto) => (row.dogChip ?? "") + (row.secondDogChip ? ` / ${row.secondDogChip}` : ""),
      },
    ],
    [clickableDogs, t, tooSmall]
  );

  const searchIdsFunction = React.useCallback(
    (sObj: any) =>
      api.searchAllIds(
        DogSearch.fromJS({
          ...sObj,
          compatibleDogId: motherId,
          languageId: currentLanguage?.languageId,
          selectOnlyMales: true,
          excludeExpoDogs: true,
        })
      ),
    [api, currentLanguage?.languageId, motherId]
  );
  const searchFunction = React.useCallback(
    (sObj: any) =>
      api.baseSearch(
        DogSearch.fromJS({
          ...sObj,
          compatibleDogId: motherId,
          languageId: currentLanguage?.languageId,
          selectOnlyMales: true,
          excludeExpoDogs: true,
          checkbreedAuthorized: true,
        })
      ),
    [api, currentLanguage?.languageId, motherId]
  );

  return (
    <Loading isLoading={loading}>
      <SRSHCustomSelector
        linkedIds={linkedIds}
        columns={columns}
        onLinkedIdsChanged={onLinkedIdsChanged}
        idField="dogId"
        searchFunction={searchFunction}
        searchIdsFunc={searchIdsFunction}
        editMode={editMode}
        showAllButtons={false}
        exportFunction={(sObj) =>
          api.exportXlsFile(
            DogSearch.fromJS({
              ...sObj,
              languageId: currentLanguage?.languageId,
              excludeSelectedIds: false,
            })
          )
        }
      />
    </Loading>
  );
};
